import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Criterion } from '@/types/evaluation.types';
import { Importance } from '@/types/evaluation.types';
import { generateId } from '@/lib/id';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ImportanceSelector } from './ImportanceSelector';

interface CriterionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (criterion: Criterion) => void;
  criterion?: Criterion;
  mode: 'add' | 'edit';
}

export function CriterionForm({
  open,
  onOpenChange,
  onSave,
  criterion,
  mode,
}: CriterionFormProps) {
  const { t } = useTranslation('criteria');

  const [formData, setFormData] = useState<Partial<Criterion>>(() => ({
    id: criterion?.id || generateId(),
    title: criterion?.title || '',
    description: criterion?.description || '',
    importance: criterion?.importance || Importance.Medium,
    category: criterion?.category || null,
    helpfulHints: criterion?.helpfulHints || '',
  }));

  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
  }>({});

  // Update form when criterion changes
  useEffect(() => {
    if (open) {
      setFormData({
        id: criterion?.id || generateId(),
        title: criterion?.title || '',
        description: criterion?.description || '',
        importance: criterion?.importance || Importance.Medium,
        category: criterion?.category || null,
        helpfulHints: criterion?.helpfulHints || '',
      });
      setErrors({});
    }
  }, [open, criterion]);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.title?.trim()) {
      newErrors.title = t('form.criterion.errors.titleRequired');
    } else if (formData.title.length > 255) {
      newErrors.title = t('form.criterion.errors.titleTooLong', { max: 255 });
    }

    if (!formData.description?.trim()) {
      newErrors.description = t('form.criterion.errors.descriptionRequired');
    } else if (formData.description.length > 1024) {
      newErrors.description = t('form.criterion.errors.descriptionTooLong', { max: 1024 });
    }

    if (formData.category && formData.category.length > 255) {
      newErrors.title = t('form.criterion.errors.categoryTooLong', { max: 255 });
    }

    if (formData.helpfulHints && formData.helpfulHints.length > 1024) {
      newErrors.description = t('form.criterion.errors.hintsTooLong', { max: 1024 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent, addAnother = false) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSave({
      id: formData.id!,
      title: formData.title!.trim(),
      description: formData.description!.trim(),
      importance: formData.importance!,
      category: formData.category?.trim() || null,
      helpfulHints: formData.helpfulHints?.trim() || undefined,
    });

    // Reset form
    setFormData({
      id: generateId(),
      title: '',
      description: '',
      importance: Importance.Medium,
      category: null,
      helpfulHints: '',
    });
    setErrors({});

    // Close dialog only if not adding another
    if (!addAnother) {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: criterion?.id || generateId(),
      title: criterion?.title || '',
      description: criterion?.description || '',
      importance: criterion?.importance || Importance.Medium,
      category: criterion?.category || null,
      helpfulHints: criterion?.helpfulHints || '',
    });
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {mode === 'add'
                ? t('form.criterion.titleAdd')
                : t('form.criterion.titleEdit')}
            </DialogTitle>
            <DialogDescription>
              {mode === 'add'
                ? t('form.criterion.descriptionAdd')
                : t('form.criterion.descriptionEdit')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="criterion-title">
                {t('form.criterion.title')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="criterion-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('form.criterion.titlePlaceholder')}
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'criterion-title-error' : undefined}
                maxLength={255}
              />
              {errors.title && (
                <p id="criterion-title-error" className="text-sm text-destructive" role="alert">
                  {errors.title}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.title?.length || 0}/255
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="criterion-description">
                {t('form.criterion.description')} <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="criterion-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('form.criterion.descriptionPlaceholder')}
                aria-invalid={!!errors.description}
                aria-describedby={errors.description ? 'criterion-description-error' : undefined}
                rows={3}
                maxLength={1024}
              />
              {errors.description && (
                <p id="criterion-description-error" className="text-sm text-destructive" role="alert">
                  {errors.description}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.description?.length || 0}/1024
              </p>
            </div>

            {/* Importance */}
            <ImportanceSelector
              value={formData.importance!}
              onChange={(importance) => setFormData({ ...formData, importance })}
            />

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="criterion-category">
                {t('form.criterion.category')}
              </Label>
              <Input
                id="criterion-category"
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder={t('form.criterion.categoryPlaceholder')}
                maxLength={255}
              />
              <p className="text-xs text-muted-foreground">
                {t('form.criterion.categoryHint')}
              </p>
            </div>

            {/* Helpful Hints */}
            <div className="space-y-2">
              <Label htmlFor="criterion-hints">
                {t('form.criterion.helpfulHints')}
              </Label>
              <Textarea
                id="criterion-hints"
                value={formData.helpfulHints || ''}
                onChange={(e) => setFormData({ ...formData, helpfulHints: e.target.value })}
                placeholder={t('form.criterion.helpfulHintsPlaceholder')}
                rows={2}
                maxLength={1024}
              />
              <p className="text-xs text-muted-foreground">
                {formData.helpfulHints?.length || 0}/1024
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleCancel}>
              {t('common:actions.cancel')}
            </Button>
            {mode === 'add' && (
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
              >
                {t('form.criterion.saveAndAddAnother')}
              </Button>
            )}
            <Button type="submit">
              {mode === 'add' ? t('common:actions.add') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
